/**
 * Hardware HID barcode scanner listener.
 * Bluetooth/USB gun-type scanners act as HID keyboards: they type all digits of
 * the barcode very fast and end with an Enter. We detect the rapid burst and
 * fire onScan(gtin) without the user having to focus any input.
 *
 * Usage:
 *   useHardwareScanner({ enabled, onScan, minLen: 8, maxLen: 14, gapMs: 40 })
 *
 * Design notes:
 *  - We use `keydown` at document level. Regular human typing (~150–250ms between
 *    keys) never triggers a scan because the gap threshold is ~40ms.
 *  - We ONLY care about digits. Any non-digit or a long pause resets the buffer.
 *  - When Enter arrives and buffer length ∈ [minLen, maxLen], we `preventDefault`
 *    to avoid the scanner accidentally submitting the page's form or firing an
 *    unrelated onKeyDown, then fire the callback.
 *  - We SKIP the listener when the user is typing inside an input/textarea/
 *    contenteditable so voice-panel and price fields keep working normally
 *    (unless `captureEverywhere=true`).
 */
import { useEffect, useRef } from "react";

export default function useHardwareScanner({
  enabled = true,
  onScan,
  minLen = 8,
  maxLen = 14,
  gapMs = 40,
  captureEverywhere = false,
} = {}) {
  const bufRef = useRef("");
  const lastKeyAtRef = useRef(0);

  useEffect(() => {
    if (!enabled || typeof onScan !== "function") return;

    const isTypingIn = (el) => {
      if (!el) return false;
      const tag = (el.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return true;
      if (el.isContentEditable) return true;
      return false;
    };

    const handler = (ev) => {
      // Respect ambient typing unless caller wants raw capture
      if (!captureEverywhere && isTypingIn(document.activeElement)) return;
      // Skip modifier combos (ctrl/alt/meta) — those are shortcuts, not scans
      if (ev.ctrlKey || ev.metaKey || ev.altKey) return;

      const now = Date.now();
      const dt = now - lastKeyAtRef.current;
      lastKeyAtRef.current = now;

      const k = ev.key;

      // End of scan
      if (k === "Enter") {
        const buf = bufRef.current;
        bufRef.current = "";
        if (buf.length >= minLen && buf.length <= maxLen) {
          ev.preventDefault();
          ev.stopPropagation();
          try { onScan(buf); } catch { /* noop */ }
        }
        return;
      }

      // Only digits are considered scan payload. Everything else resets.
      if (!/^[0-9]$/.test(k)) {
        // Ignore Shift/CapsLock/Tab/etc; only reset on human-visible characters
        if (k.length === 1) bufRef.current = "";
        return;
      }

      // Gap between keys too big → this is a human typing. Reset & start fresh.
      if (dt > gapMs && bufRef.current.length > 0) {
        bufRef.current = "";
      }
      bufRef.current += k;
      if (bufRef.current.length > maxLen) {
        // Overflow — drop oldest digit
        bufRef.current = bufRef.current.slice(-maxLen);
      }
    };

    document.addEventListener("keydown", handler, true);
    return () => document.removeEventListener("keydown", handler, true);
  }, [enabled, onScan, minLen, maxLen, gapMs, captureEverywhere]);
}
